import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import type { Announcement, Role } from "@/types/db";
import { fmtDate, isAdmin } from "@/lib/utils";
import { RichTextEditor, AutoText } from "@/components/RichText";

const ALL_ROLES: Role[] = ["employee", "admin", "accountant", "super_admin"];

export function Announcements() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<Announcement[]>([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Partial<Announcement> | null>(null);
  const admin = isAdmin(employee?.role);

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    const ch = supabase.channel("announcements")
      .on("postgres_changes", { event: "*", schema: "public", table: "hq_announcements" }, () => void load())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, []);

  async function load() {
    const { data } = await supabase.from("hq_announcements").select("*")
      .order("pinned", { ascending: false })
      .order("created_at", { ascending: false });
    setRows((data as Announcement[]) ?? []);
  }

  async function save() {
    if (!editing || !editing.title || !editing.content || !employee) return;
    const payload = {
      title: editing.title,
      content: editing.content,
      pinned: editing.pinned ?? false,
      priority: editing.priority ?? "normal",
      visible_to_roles: editing.visible_to_roles ?? ALL_ROLES,
      created_by: employee.id,
    };
    if (editing.id) await supabase.from("hq_announcements").update(payload).eq("id", editing.id);
    else await supabase.from("hq_announcements").insert(payload);
    setEditing(null);
    await load();
  }

  async function del(id: string) {
    if (!confirm("Delete this announcement?")) return;
    await supabase.from("hq_announcements").delete().eq("id", id);
    await load();
  }

  const filtered = q
    ? rows.filter(r => `${r.title} ${r.content}`.toLowerCase().includes(q.toLowerCase()))
    : rows;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Announcements</h1>
        {admin && (
          <button
            onClick={() => setEditing({ title: "", content: "", priority: "normal", pinned: false, visible_to_roles: ALL_ROLES })}
            className="btn"
          >+ New</button>
        )}
      </div>

      <input className="input" placeholder="Search announcements…" value={q} onChange={e => setQ(e.target.value)} />

      {filtered.length === 0 ? (
        <div className="card py-12 text-center text-sm text-gray2">No announcements yet.</div>
      ) : (
        filtered.map(a => (
          <article key={a.id} className={`card relative ${a.pinned ? "ring-2 ring-teal" : ""}`}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  {a.pinned && <span className="pill pill-mint">📌 Pinned</span>}
                  {a.priority === "urgent" && <span className="pill pill-bad">URGENT</span>}
                  {a.priority === "important" && <span className="pill pill-warn">IMPORTANT</span>}
                </div>
                <h2 className="text-xl font-bold">{a.title}</h2>
                <div className="text-xs text-gray2">{fmtDate(a.created_at)}</div>
              </div>
              {admin && (
                <div className="flex gap-1">
                  <button onClick={() => setEditing(a)} className="btn btn-sm btn-ghost">Edit</button>
                  <button onClick={() => del(a.id)} className="btn btn-sm btn-ghost text-bad ring-bad">×</button>
                </div>
              )}
            </div>
            <AutoText text={a.content} className="text-sm leading-relaxed" />
          </article>
        ))
      )}

      {editing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <h2 className="mb-4 text-xl font-bold">{editing.id ? "Edit" : "New"} announcement</h2>
            <div className="space-y-4">
              <div><label className="label">Title</label><input className="input" value={editing.title ?? ""} onChange={e => setEditing({ ...editing, title: e.target.value })} autoFocus /></div>
              <div><label className="label">Content</label><RichTextEditor value={editing.content ?? ""} onChange={html => setEditing({ ...editing, content: html })} minHeight={180} placeholder="Write your announcement…" /></div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Priority</label>
                  <select className="input" value={editing.priority ?? "normal"} onChange={e => setEditing({ ...editing, priority: e.target.value as Announcement["priority"] })}>
                    <option value="normal">Normal</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <label className="flex items-end gap-2 pb-2">
                  <input type="checkbox" checked={editing.pinned ?? false} onChange={e => setEditing({ ...editing, pinned: e.target.checked })} />
                  Pin to top
                </label>
              </div>
              <div>
                <label className="label">Visible to roles</label>
                <div className="flex flex-wrap gap-3">
                  {ALL_ROLES.map(r => {
                    const checked = (editing.visible_to_roles ?? []).includes(r);
                    return (
                      <label key={r} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={checked} onChange={e => {
                          const cur = editing.visible_to_roles ?? [];
                          setEditing({ ...editing, visible_to_roles: e.target.checked ? [...cur, r] : cur.filter(x => x !== r) });
                        }} />
                        {r}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="btn btn-ghost">Cancel</button>
                <button onClick={save} className="btn" disabled={!editing.title || !editing.content}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
